import React from "react";
import styled from "styled-components";
import { toast } from "react-toastify";

import { formatDate } from "src/library/utils/moment";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import { Modal } from "src/library/components/Modal";
import { Icons } from "src/library/components/Icon/types";
import {
  IProductSubstitution,
  useSubstitutionsApi,
  ProductSubstitutionStatus
} from "src/modules/substitutions/api/substitutions.api";
import { PageProps } from "src/pages/Router/routes";
import { SelectedProductCard } from "src/core/products/components/ProductCard";
import { SubstitutionStatus } from "src/modules/substitutions/components/SubstitutionStatus";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

import { ProductSubstitutionPageProps } from ".";

interface SubstitutionTabProps {
  substitution: IProductSubstitution;
}

export const SubstitutionTab: React.ReactFCWithChildren<PageProps<SubstitutionTabProps, ProductSubstitutionPageProps>> = ({ history, substitution }) => {
  const checkPermission = useCheckUserPermission();
  const canAmendSub = checkPermission("amend-substitution");
  const canDeleteSub = checkPermission("delete-substitution");
  // API
  const [ deleteSubstitutionResponse, deleteSubstitutionRequest ] = useSubstitutionsApi("DELETE_SUBSTITUTION");
  // state
  const [ deleteModalVisible, setDeleteModalVisible ] = React.useState(false);

  const onDeleteSubstitution = () => {
    deleteSubstitutionRequest({ substitutionId: substitution.id });
  };

  const onEditSubstitution = () => history.push(`/admin/products/substitutions/${substitution.id}/update`);

  React.useEffect(() => {
    if (deleteSubstitutionResponse.error) {
      toast.error(deleteSubstitutionResponse.error.message);
    }
  }, [ deleteSubstitutionResponse.error ]);

  React.useEffect(() => {
    if (deleteSubstitutionResponse.data) {
      toast.success("Substitution deleted successfully!");
      history.push("/admin/products/substitutions");
    }
  }, [ deleteSubstitutionResponse.data, history ]);

  return (
    <Wrapper>
      <InfoBlock>
        <LabelText>
          Status
        </LabelText>

        <SubstitutionStatus status={substitution.status} />
      </InfoBlock>

      {substitution.previousProduct && (
        <InfoBlock>
          <LabelText>
            Unavailable Product
          </LabelText>

          <SelectedProductCard
            product={substitution.previousProduct}
            icon={Icons.CHEVRONRIGHT}
            onClick={() => history.push(`/products/${substitution.previousProductId}`)}
          />
        </InfoBlock>
      )}

      {substitution.replacementProduct && (
        <InfoBlock>
          <LabelText>
            Replacement Product
          </LabelText>

          <SelectedProductCard
            product={substitution.replacementProduct}
            icon={Icons.CHEVRONRIGHT}
            onClick={() => history.push(`/products/${substitution.replacementProductId}`)}
          />
        </InfoBlock>
      )}

      <InfoBlock>
        <LabelText>
          Substitution window
        </LabelText>

        <Text>
          {`${formatDate(substitution.startDate, "DD MMM YYYY")} – ${formatDate(substitution.endDate, "DD MMM YYYY")}`}
        </Text>
      </InfoBlock>

      {substitution.reasonCode && (
        <InfoBlock>
          <LabelText>
            Reason
          </LabelText>

          <Text>
            {substitution.reasonCode?.description}
          </Text>
        </InfoBlock>
      )}

      <InfoBlock>
        <LabelText>
          Message
        </LabelText>

        <Text>
          {substitution.message}
        </Text>
      </InfoBlock>

      <InfoBlock>
        <LabelText>
          Notifications
        </LabelText>

        <Text>
          {substitution.notifyUsers ? "Notifications were sent to users" : "Notifications were not sent to users"}
        </Text>
      </InfoBlock>

      {substitution.status !== ProductSubstitutionStatus.Deleted && (
        <InfoBlock>
          <ButtonRow>
            {canAmendSub && (
              <Button onClick={onEditSubstitution}>
                Edit
              </Button>
            )}

            {canDeleteSub && (
              <Button
                $buttonType="error"
                onClick={() => setDeleteModalVisible(true)}
              >
                Delete
              </Button>
            )}
          </ButtonRow>
        </InfoBlock>
      )}

      <Modal
        small
        type="confirm"
        show={deleteModalVisible}
        title="Delete substitution"
        body="Are you sure you want to delete this substitution? This action cannot be undone."
        actions={[
          {
            name: "Cancel",
            action: () => setDeleteModalVisible(false),
            $buttonType: "secondary"
          },
          {
            name: "Delete",
            action: () => {
              onDeleteSubstitution();
              setDeleteModalVisible(false);
            },
            $buttonType: "primary"
          }
        ]}
        backgroundAction={() => setDeleteModalVisible(false)}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBlock = styled(Wrapper)`
  &:not(:first-of-type) {
    margin-top: 3rem;
  }

  & > div:not(:first-child) {
    background-color: ${({ theme }) => theme.colors.lightestGrey}88;
  }
`;

const LabelText = styled(Text).attrs({ color: "secondary" })`
  margin-bottom: 1rem;
`;

const ButtonRow = styled.div`
  padding: 1rem 0;
  display: flex;
  flex-direction: row;
  align-items: center;

  & > :not(:first-child) {
    margin-left: 2rem;
  }
`;
